import React from 'react';
import { useTranslation } from 'react-i18next';

const clockOf = (minutes) => {
  const m = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h} h ${String(mm).padStart(2, '0')}` : `${h} h`;
};

const minutesOf = (value, fallback) => {
  if (value === '' || value === undefined || value === null) return fallback;
  const n = Math.round(Number(value));
  return Number.isFinite(n) ? Math.max(0, Math.min(720, n)) : fallback;
};

/** What a client is told for an appointment booked at `at` (minutes from 0 h). */
export function announcedTime(settings, at, firstOfDay) {
  let before = minutesOf(settings.appointment_window_before, 0);
  let after = minutesOf(settings.appointment_window_after, 0);
  if (firstOfDay) {
    before = minutesOf(settings.appointment_first_window_before, before);
    after = minutesOf(settings.appointment_first_window_after, after);
  }
  if (!before && !after) return clockOf(at);
  return { start: clockOf(at - before), end: clockOf(at + after) };
}

/**
 * « Tranche horaire »: minutes before / after the booked time that the client
 * is told, and a separate (usually narrower) window for the first appointment
 * of the day. A live example shows what a client will read.
 */
export default function ArrivalWindowSettings({ settings, onChange }) {
  const { t } = useTranslation();
  const field = (key, label, placeholder) => (
    <div className="window-field">
      <label htmlFor={`window-${key}`}>{label}</label>
      <div className="window-input">
        <input
          id={`window-${key}`}
          className="form-control"
          type="number"
          inputMode="numeric"
          min="0"
          max="720"
          step="5"
          placeholder={placeholder}
          value={settings[key] ?? ''}
          onChange={(e) => onChange(key, e.target.value)}
        />
        <span aria-hidden="true">min</span>
      </div>
    </div>
  );
  const say = (v) => (typeof v === 'string' ? v : t('admin.windowBetween', v));
  const general = announcedTime(settings, 13 * 60, false);
  const first = announcedTime(settings, 8 * 60 + 30, true);
  const generalBefore = minutesOf(settings.appointment_window_before, 0);
  const generalAfter = minutesOf(settings.appointment_window_after, 0);

  return (
    <fieldset className="window-settings">
      <legend>{t('admin.windowTitle')}</legend>
      <p className="window-hint">{t('admin.windowHint')}</p>
      <div className="window-row">
        <span className="window-row-label">{t('admin.windowAll')}</span>
        {field('appointment_window_before', t('admin.windowBefore'), '0')}
        {field('appointment_window_after', t('admin.windowAfter'), '0')}
      </div>
      <div className="window-row">
        <span className="window-row-label">{t('admin.windowFirst')}</span>
        {field('appointment_first_window_before', t('admin.windowBefore'), String(generalBefore))}
        {field('appointment_first_window_after', t('admin.windowAfter'), String(generalAfter))}
      </div>
      <p className="window-example" aria-live="polite">
        {t('admin.windowExample', { general: say(general), first: say(first) })}
      </p>
    </fieldset>
  );
}
